import Control from "ol/control/Control";
import { distanceAlongCm, row, rowHeader, rowHint, toLatLon } from "../util";
import {
  curvatureAtJunctionKeyValues,
  maxSpeedDisplayFromCurvature,
  orbisCurvatureToNds,
} from "../layers/style/curvature";
import { lineString, point } from "@turf/helpers";
import { pointToLineDistance } from "@turf/turf";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { LineString } from "ol/geom";
import { LRChainEncoded, ValueAtProducer } from "@orbis-lr-sdk/orbis-lr-sdk";
import { buildTangentFeature } from "../layers/HoverLayer";
import { splitLaneList } from "../layers/style/util/laneBands.js";
import {
  isActiveAt,
  parseTimeDomain,
  splitConditionalValue,
} from "../layers/style/util/conditionalValues.mjs";

export class FeatureInfoControl extends Control {
  constructor() {
    const div = document.createElement("div");
    super({
      element: div,
    });
    const this_ = this;
    this.handlersForLayers = [];
    this.hoverLayersForLayers = [];
    div.id = "feature-info";
    div.className = "ol-unselectable";
    this.attributesTable = document.createElement("table");
    this.attributesTable.className = "ol-selectable feature-info-list";
    div.append(this.attributesTable);

    // listen to changes
    this.on("propertychange", () => {
      // roles
      const selectedFeaturesAndLayers = this_.get("selectedFeaturesAndLayers");
      if (selectedFeaturesAndLayers === undefined) {
        this.element.style.display = "none";
        return;
      }
      const selectedFeatures = selectedFeaturesAndLayers.map(
        (featureAndLayer) => featureAndLayer.feature,
      );
      const position = this_.get("position");
      const fcdSpeeds = this_.get("fcdSpeeds");
      this.showValuesAtPosition(position, selectedFeatures, fcdSpeeds);
      selectedFeaturesAndLayers.forEach((featureAndLayer) => {
        const feature = featureAndLayer.feature;
        const layer = featureAndLayer.layer;
        this_
          .getHandlersForLayer(layer)
          .forEach((handler) => handler(feature, layer, position));
        const hoverLayers = this_.getHoverLayersForLayer(layer);
        if (hoverLayers.length === 0) return;
        const built = buildTangentFeature(feature, position);
        if (!built) return;
        const keyValuesAt = this.getKeyValuesAt(
          feature,
          distanceAlongCm(built.latLonGeometry, built.closestPointLatLon),
        );
        keyValuesAt.forEach(([k, v]) => built.tangent.set(k, v));
        hoverLayers.forEach((hoverLayer) => {
          hoverLayer.getSource().clear();
          hoverLayer.getSource().addFeature(built.tangent);
        });
      });
    });
  }

  getHandlersForLayer(layer) {
    return this.handlersForLayers
      .filter((layerAndHandler) => layerAndHandler.layer === layer)
      .map((layerAndHandler) => layerAndHandler.handler);
  }
  getHoverLayersForLayer(layer) {
    return this.hoverLayersForLayers
      .filter((layerAndHoverLayer) => layerAndHoverLayer.layer === layer)
      .map((layerAndHoverLayer) => layerAndHoverLayer.hoverLayer);
  }
  getAllHoverLayers() {
    return this.hoverLayersForLayers.map(
      (layerAndHoverLayer) => layerAndHoverLayer.hoverLayer,
    );
  }
  addHandlerForLayer(handler, layer) {
    this.handlersForLayers.push({ layer: layer, handler: handler });
  }
  addHoverLayerForLayer(hoverLayer, layer) {
    this.hoverLayersForLayers.push({ layer: layer, hoverLayer: hoverLayer });
  }

  showValuesAtPosition(position, selectedFeatures, fcdSpeeds) {
    const hasFcd = fcdSpeeds && Object.keys(fcdSpeeds).length > 0;
    const closestFeature =
      position !== undefined && selectedFeatures !== undefined
        ? this.findClosestLineFeature(selectedFeatures, position)
        : null;
    if (!closestFeature) {
      if (!hasFcd) {
        this.element.style.display = "none";
        return;
      }
      this.refreshAttributes(null, null, [], fcdSpeeds);
      return;
    }
    const latLonGeometry = toLatLon(
      lineString(closestFeature.getGeometry().getCoordinates()).geometry,
    );
    const closestPointLatLon = nearestPointOnLine(
      latLonGeometry,
      toLatLon(point(position).geometry),
    );
    const distanceAtFeatureCm = distanceAlongCm(
      latLonGeometry,
      closestPointLatLon,
    );
    const keyValuesAt = this.getKeyValuesAt(
      closestFeature,
      distanceAtFeatureCm,
    );
    const featureLengthCm = closestFeature.get("lengthCm");
    this.refreshAttributes(
      distanceAtFeatureCm,
      featureLengthCm,
      keyValuesAt,
      fcdSpeeds,
    );
  }

  findClosestLineFeature(selectedFeatures, position) {
    const latLonEventPoint = toLatLon(point(position).geometry);
    return Array.from(selectedFeatures)
      .filter((feature) => feature.getGeometry() instanceof LineString)
      .sort(
        (f1, f2) =>
          pointToLineDistance(
            latLonEventPoint,
            lineString(f1.getGeometry().getCoordinates()).geometry,
          ) -
          pointToLineDistance(
            latLonEventPoint,
            lineString(f2.getGeometry().getCoordinates()).geometry,
          ),
      )[0];
  }

  refreshAttributes(
    distanceAtFeatureCm,
    featureLengthCm,
    keyValuesAt,
    fcdSpeeds,
  ) {
    this.attributesTable.innerHTML = "";
    const colGroup = document.createElement("colgroup");
    const colLabel = document.createElement("col");
    colLabel.classList.add("label");
    const colValue = document.createElement("col");
    colValue.classList.add("value");
    colGroup.appendChild(colLabel);
    colGroup.appendChild(colValue);
    this.attributesTable.appendChild(colGroup);
    this.element.style.display = "block";
    this.attributesTable.style = "display: table;";
    if (distanceAtFeatureCm !== null) {
      this.attributesTable.append(
        rowHeader(
          "position " + distanceAtFeatureCm + " / " + featureLengthCm + " cm",
        ),
      );
      keyValuesAt.forEach((keyValueAt) =>
        this.attributesTable.append(row(keyValueAt[0], keyValueAt[1])),
      );
      this.attributesTable.append(rowHint("click feature to see all tags"));
    }
    if (fcdSpeeds && Object.keys(fcdSpeeds).length > 0) {
      this.attributesTable.append(rowHeader("FCD speed at cursor"));
      Object.keys(fcdSpeeds)
        .map((id) => Number(id))
        .sort((a, b) => a - b)
        .forEach((id) => {
          const arrow = document.createElement("span");
          arrow.textContent = "↑";
          arrow.style.display = "inline-block";
          arrow.style.transform = `rotate(${id * 30}deg)`;
          this.attributesTable.append(row(arrow, fcdSpeeds[id] + " km/h"));
        });
    }
  }

  getKeyValuesAt(feature, distanceAtFeatureCm) {
    const keyValuesAt = feature
      .getKeys()
      .filter((key) => key !== "geometry")
      .filter((key) => key !== "type")
      .filter((key) => key !== "lengthCm")
      .filter((key) => key !== "meta-uuid")
      .filter((key) => !key.startsWith("revision-"))
      .filter((key) => {
        const value = feature.get(key);
        return new LRChainEncoded(key, value?.toString() ?? "").isLrEncoded();
      })
      .map((key) => {
        const value = feature.get(key);
        const valueAt = ValueAtProducer.getValueAt(
          key,
          value?.toString(),
          distanceAtFeatureCm,
        );
        return [key, valueAt];
      });
    // Append derived radius and max speed for LR-encoded curvature
    const curvatureEntry = keyValuesAt.find(
      ([key]) =>
        key.endsWith("-curvature:linear") || key.endsWith("-curvature"),
    );
    if (curvatureEntry) {
      const curvature = Number(curvatureEntry[1]);
      if (curvature !== 0 && !isNaN(curvature)) {
        const radius = 10000 / Math.abs(curvature);
        keyValuesAt.push(["NDS curvature", orbisCurvatureToNds(curvature)]);
        keyValuesAt.push(["radius", Math.round(radius) + " m"]);
        keyValuesAt.push([
          "max speed (based on curvature)",
          maxSpeedDisplayFromCurvature(curvature),
        ]);
      }
    }

    // Append curvature_at_junction (plain numeric, not LR-encoded) + derived values
    keyValuesAt.push(
      ...curvatureAtJunctionKeyValues(feature.get("curvature_at_junction")),
    );

    // Append readable per-lane rows for #Conditional HOV. Read from the FEATURE, not
    // from keyValuesAt: whether the raw row survived the isLrEncoded filter is the
    // SDK's call, but the derived summary must not depend on it.
    keyValuesAt.push(...hovConditionalKeyValues(feature, distanceAtFeatureCm));

    return keyValuesAt;
  }
}

// The position-resolved value of the first feature key containing `part` (the
// step-sectioned variant carries a `:step` suffix, so a contains-test finds both).
function conditionalValueAt(feature, part, distanceAtFeatureCm) {
  const key = feature
    .getKeys()
    .filter((key) => !key.startsWith("revision-"))
    .find((key) => key.includes(part));
  if (!key) return undefined;
  return ValueAtProducer.getValueAt(
    key,
    feature.get(key)?.toString(),
    distanceAtFeatureCm,
  );
}

// One row per HOV lane: its time window, the minimum occupancy, and — when the
// window parses — whether it is active at the browser's clock. The window is written
// in the ROAD's local time, so the row names the timezone it was evaluated in
// instead of pretending the two clocks agree.
function hovConditionalKeyValues(feature, distanceAtFeatureCm) {
  const lanesValue = conditionalValueAt(
    feature,
    "-hov:lanes:conditional",
    distanceAtFeatureCm,
  );
  if (lanesValue === undefined) return [];
  const minimumsValue = conditionalValueAt(
    feature,
    "-hov:minimum:lanes:conditional",
    distanceAtFeatureCm,
  );
  const lanes = splitLaneList(lanesValue);
  const minimums = splitLaneList(minimumsValue);
  const now = new Date();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rows = [];
  lanes.forEach((slot, index) => {
    const parsed = splitConditionalValue(slot);
    if (!parsed) return;
    const minimum = splitConditionalValue(minimums[index] ?? "");
    let text = parsed.condition ?? "always";
    if (minimum?.value) {
      text += ` · min occupancy ${minimum.value}`;
    }
    const active = isActiveAt(parseTimeDomain(parsed.condition), now);
    if (active !== null) {
      text += ` · active now (${timeZone}): ${active ? "yes" : "no"}`;
    }
    rows.push([`HOV window, lane ${index + 1} of ${lanes.length}`, text]);
  });
  return rows;
}
